import { AuthFetchWithResponse } from 'src/Types/api'
import { parseInstallationConfigPutPayload } from '../../parsers'
import { ApiResult, throwApiError } from 'src/Libs/api'
import { StatusCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { store } from 'src/State/Store'

export interface InstallationConfigDto {
  connectionDerated: number
  coordinateChargingUseCase: boolean
  countryCode: string
  createdAt: string
  createdBy: number
  deletedAt: string
  eegActive: boolean
  eegConnectedPower: number
  eegDerateAtConnectedPower: boolean
  eegValue: number
  emsBatteryBeforeCarMode: boolean
  emsBatteryToCarMode: boolean
  emsWallboxEnforcePowerAssignment: boolean
  emsWeatherRegulatedChargeEnabled: boolean
  id: number
  installationId: number
  mainFuse: number
  mainFuseDerated: number
  peakPowerPv: number
  phaseCount: 1 | 3
  phaseImbalance: boolean
  phaseImbalanceValue: number
  powerMeterType: string
  preFuse: number
  selfconsumptionUseCase: boolean
  updatedAt: string
  updatedBy: number
  rootPowerMeter: number // * the id of the deviceLink that is used for the wiring topology
  sgrCooldown: boolean // * Advanced Settings - Weather forecast optimization sgReady or battery
  emergencyPowerCheckPartialGridFailure: boolean // * Advanced Settings - Emergency Power - active
  emergencyPowerDelayInSeconds: number // * Advanced Settings - Emergency Power - delay
}

export const getInstallationConfig = async (
  installationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<Nullable<InstallationConfigDto> | null>> => {
  try {
    const response = await authFetch<Nullable<InstallationConfigDto>>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/config`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<Nullable<InstallationConfigDto>>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get installation config:')
  }
}
// This is the PUT DTO as it is in Swagger. It is outdated and does not represent the currenly used DTO.
// Correct DTO is bellow.
// type InstallationConfigPUTDto = Pick<
//   InstallationConfigDto,
//   | "preFuse"
//   | "connectionDerated"
//   | "powerMeterType"
//   | "countryCode"
//   | "mainFuse"
//   | "mainFuseDerated"
//   | "eegActive"
//   | "eegValue"
//   | "eegDerateAtConnectedPower"
//   | "eegConnectedPower"
//   | "phaseCount"
//   | "peakPowerPv"
//   | "emsWeatherRegulatedChargeEnabled"
//   | "emsBatteryBeforeCarMode"
//   | "emsBatteryToCarMode"
//   | "emsWallboxEnforcePowerAssignment"
//   | "emsOperatingRangeAuto"
//   | "emsMaxChargePower"
//   | "emsMaxDischargePower"
//   | "emsDischargeStartPower"
//   | "emsPowersaveEnabled"
//   | "selfconsumptionUseCase"
//   | "coordinateChargingUseCase"
//   | "phaseImbalance"
//   | "phaseImbalanceValue"
// >

export type InstallationConfigPUTDto = Pick<
  Nullable<InstallationConfigDto>,
  | 'eegValue'
  | 'eegConnectedPower'
  | 'eegDerateAtConnectedPower'
  | 'emsWeatherRegulatedChargeEnabled'
  | 'emsBatteryBeforeCarMode'
  | 'emsBatteryToCarMode'
  | 'emsWallboxEnforcePowerAssignment'
  | 'peakPowerPv'
  | 'phaseCount'
  | 'mainFuse'
  | 'mainFuseDerated'
  | 'coordinateChargingUseCase'
  | 'phaseImbalance'
  | 'phaseImbalanceValue'
  | 'selfconsumptionUseCase'
  | 'countryCode'
  | 'rootPowerMeter'
  | 'sgrCooldown'
  | 'emergencyPowerCheckPartialGridFailure'
  | 'emergencyPowerDelayInSeconds'
>

export const updateInstallationConfig = async (
  installationId: number,
  config: Partial<InstallationConfigPUTDto>,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  const userLevel = store.getState().user.userData?.level
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/config`,
      method: 'PUT',
      payload: parseInstallationConfigPutPayload(config, userLevel),
    })

    if (![StatusCodes.OK, StatusCodes.NO_CONTENT].includes(response.status)) {
      return new ApiResult<null, Error | AuthFetchError>(
        null,
        true,
        new Error(`Unexpected status code ${response.status} when updating installation config`)
      )
    }

    return new ApiResult<null>(null)
  } catch (error) {
    throw new Error('Error updating installation config: ' + (error as Error)?.message)
  }
}

export interface EmcIdlePeriodDto {
  mode: 'charge' | 'discharge'
  from: string
  to: string
  days: {
    mo: boolean
    tu: boolean
    we: boolean
    th: boolean
    fr: boolean
    sa: boolean
    su: boolean
  }
}

export const getEmcIdlePeriods = async (
  installationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<Nullable<EmcIdlePeriodDto>[] | null>> => {
  try {
    const response = await authFetch<Nullable<EmcIdlePeriodDto>[]>({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/config/ems-idle-periods`,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<Nullable<EmcIdlePeriodDto>[]>(response.data)
  } catch (error) {
    return throwApiError(error, 'Failed to get EMC idle periods:')
  }
}

export const updateEmcIdlePeriods = async (
  installationId: number,
  idlePeriods: EmcIdlePeriodDto[],
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch({
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      path: `installations/${installationId}/config/ems-idle-periods`,
      method: 'PUT',
      payload: idlePeriods,
    })

    if (response.status !== StatusCodes.OK) {
      return new ApiResult<null, Error | AuthFetchError>(null)
    }

    return new ApiResult<null>(null)
  } catch (error) {
    return throwApiError(error, 'Failed to update EMC idle periods:')
  }
}
